import { z } from "zod/v4";

import { dateKeySchema } from "./amenity";
import { filterablePassStatusSchema } from "./pass-status";

/** Where an invite was authored. "email" = the inbound visit-announcement
 *  path (spec 2026-08-27-inbound-visit-email-design.md).
 *
 *  Lives here (not index.ts) rather than in index.ts: `inviteSearchFiltersSchema`
 *  below needs it, and importing it back from index.ts would be a load-time
 *  cycle (index.ts re-exports this module). Re-exported from index.ts so
 *  every existing `@gately/validators` import keeps working. */
export const inviteSources = ["app", "email"] as const;
export const inviteSourceSchema = z.enum(inviteSources);
export type InviteSource = (typeof inviteSources)[number];

const many = <T extends z.ZodTypeAny>(inner: T) => z.array(inner).min(1).max(50).optional();

export const INVITE_SEARCH_SORTS = [
  "created_desc",
  "created_asc",
  "valid_from_desc",
  "valid_from_asc",
] as const;
export type InviteSearchSort = (typeof INVITE_SEARCH_SORTS)[number];

export const inviteSearchFiltersSchema = z.object({
  /** Guest name, guest email, pass code, host name. */
  q: z.string().trim().min(1).max(120).optional(),
  status: many(filterablePassStatusSchema),
  type: many(z.enum(["one_time", "date_range", "recurring"])),
  /** Unit ids; the literal "org" selects building-level (unitless) invites. */
  unitId: many(z.string().min(1)),
  createdById: many(z.string().min(1)),
  source: many(inviteSourceSchema),
  /** At least one self-fill visitor. */
  selfFill: z.boolean().optional(),
  /** Validity window OVERLAPS [from, to], org-local days. */
  from: dateKeySchema.optional(),
  to: dateKeySchema.optional(),
});
export type InviteSearchFilters = z.infer<typeof inviteSearchFiltersSchema>;

export const inviteSearchInput = inviteSearchFiltersSchema
  .extend({
    sort: z.enum(INVITE_SEARCH_SORTS).optional().default("created_desc"),
    cursor: z.string().nullish(),
    limit: z.number().int().min(1).max(100).optional().default(50),
  })
  .prefault({});
export type InviteSearchInput = z.infer<typeof inviteSearchInput>;

/** Same shape as `inviteSearchFiltersSchema` — `status` included. The handler
 *  splits it in two: the per-status breakdown (`byStatus`) is computed with
 *  `status` stripped, so a caller reusing one filter-state object for both
 *  `search` and `searchCounts` never has the breakdown narrowed by its own
 *  status filter; `total` is computed WITH `status`, since the footer count
 *  and the view chips ("Aujourd'hui (N)") need the distinct-invite count
 *  under every filter the user actually applied, status included. */
export const inviteSearchCountsInput = inviteSearchFiltersSchema.prefault({});
